import React, { useState, useMemo, createContext, useContext, forwardRef, isValidElement, cloneElement, ReactNode } from 'react';
import {
  FloatingPortal,
  Placement,
  autoUpdate,
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useMergeRefs,
  useRole,
} from "@floating-ui/react";

const tooltipStyle = {
  background: "#4f515c",
  boxShadow: "0px 0px 14px rgba(9, 13, 22, 0.14)",
  borderRadius: "0.5rem",
  color: "#ffffff",
  fontSize: "0.75rem",
  fontWeight: "normal",
  padding: "0.5rem 1rem",
  width: "max-content",
  maxWidth: "20rem",
  boxSizing: "border-box",
  fontFamily: "Roboto, sans-serif",
  zIndex: 4,
  display: "flex",
  overflow: "hidden",
  wordBreak: "break-word"
};



type UseTooltipOptions = {
  initialOpen?: boolean;
  placement?: Placement;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

function useTooltip({
  initialOpen = false,
  placement,
  open: controlledOpen,
  onOpenChange: setControlledOpen,
}: UseTooltipOptions = {}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(initialOpen);

  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = setControlledOpen ?? setUncontrolledOpen;

  const data = useFloating({
    placement,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(5),
      flip({
        fallbackAxisSideDirection: 'start',
      }),
      shift({ padding: 5 }),
    ],
  });

  const context = data.context;

  const hover = useHover(context, {
    move: false,
    enabled: controlledOpen == null,
  });
  const focus = useFocus(context, {
    enabled: controlledOpen == null,
  });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });

  const interactions = useInteractions([hover, focus, dismiss, role]);

  return useMemo(
    () => ({
      open,
      setOpen,
      ...interactions,
      ...data,
    }),
    [open, setOpen, interactions, data]
  );
}

type TooltipContextType = ReturnType<typeof useTooltip> | null;

const TooltipContext = createContext<TooltipContextType>(null);

const useTooltipContext = () => {
  const context = useContext(TooltipContext);

  if (context == null) {
    throw new Error('Tooltip components must be wrapped in <Tooltip />');
  }

  return context;
};

type TooltipProviderProps = {
  children: ReactNode;
  initialOpen?: boolean;
  placement?: Placement;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

function TooltipProvider({
  children,
  ...options
}: TooltipProviderProps) {
  const tooltip = useTooltip(options);
  return <TooltipContext.Provider value={tooltip}>{children}</TooltipContext.Provider>;
}

type TooltipTriggerProps = {
  children: ReactNode;
  asChild?: boolean;
  icon?: ReactNode;
};

const TooltipTrigger = forwardRef<HTMLDivElement, TooltipTriggerProps>(
  function TooltipTrigger(
    { children, asChild = false, icon, ...props },
    propRef
  ) {
    const context = useTooltipContext();
    const childrenRef = (children as React.ComponentPropsWithRef<any>).ref;
    const ref = useMergeRefs([context.refs.setReference, propRef, childrenRef]);
    
    // `asChild` allows the user to pass any element as the anchor
    if (asChild && isValidElement(children)) {
      return cloneElement(
        children as React.ReactElement,
        context.getReferenceProps({
          ref,
          ...props,
          ...children.props,
          'data-state': context.open ? 'open' : 'closed',
        })
      );
    }

    return (
      <div
        ref={ref}
        style={
          {
            display: "flex"
          }
        }
        data-state={context.open ? 'open' : 'closed'}
        {...context.getReferenceProps(props)}
      >
        {children}
      </div>
    );
  }
);

type TooltipContentProps = {
  styles?: React.CSSProperties;
  [key: string]: any;
};

const TooltipContent = forwardRef<HTMLDivElement, TooltipContentProps>(
  function TooltipContent(props, propRef) {
    const context = useTooltipContext();
    const ref = useMergeRefs([context.refs.setFloating, propRef]);
    
    const containerStyle: React.CSSProperties = {
      position: context.strategy,
      top: context.y || 0,
      left: context.x || 0,
      visibility: context.x == null ? 'hidden' : 'visible',
      ...props.styles,
    }

    return (
      <FloatingPortal>
        {context.open && (
          <div
            ref={ref}
            style={containerStyle}
            {...context.getFloatingProps(props)}
          />
        )}
      </FloatingPortal>
    );
  }
);

type TooltipProps = {
  label: ReactNode;
  children: ReactNode;
  icon?: ReactNode;
  asChild?: boolean;
  [key: string]: any;
};

export const Tooltip = ({
  label,
  children,
  icon,
  ...props
}: TooltipProps) => {
  return (
    <TooltipProvider {...props}>
      <TooltipTrigger icon={icon}>
        {children}
      </TooltipTrigger>
      <TooltipContent styles={tooltipStyle}>{label}</TooltipContent>
    </TooltipProvider>
  );
};
